import styled, { keyframes } from 'styled-components';

import EmbraceSvg from '../assets/icons/EmbraceSvg';
import theme from '../assets/theme';

/**
 * Shared "The dog ate this page" fallback (design variation 13).
 *
 * Shown both when the app-level render boundary in `Client500` catches a
 * crash, and when `pages/_error.tsx` handles a non-404 failure — including a
 * client-side `/_next/data` fetch that rejects with no HTTP status. There is
 * no `pages/500.tsx`; this is the user-facing 500.
 */
const ErrorPage = () => {
  return (
    <Page>
      <StickerWrap aria-hidden="true">
        <EmbraceSvg />
      </StickerWrap>
      <Headline>The dog ate this page.</Headline>
      <Body>
        Ok, not quite — something broke on our end. Please try again, and if you&apos;re
        still stuck, reach out to us!
      </Body>
      <TryAgainButton
        type="button"
        onClick={() => {
          // A full document load. `/_next/data` (the request ad blockers and
          // Brave Shields drop) is only used for client transitions; reload
          // asks for the HTML document. It is also the way back when
          // `Client500` has already torn down the tree it wraps in `_app`.
          window.location.reload();
        }}
      >
        Try again
      </TryAgainButton>
      <SupportLine>
        Still stuck? Message us in the chat or email{' '}
        <a href="mailto:support@curipod.com">support@curipod.com</a>
      </SupportLine>
    </Page>
  );
};

const float = keyframes`
  0%,
  100% {
    transform: translateY(0) rotate(-4deg);
  }
  50% {
    transform: translateY(-10px) rotate(-1deg);
  }
`;

const ease = 'cubic-bezier(0.645, 0.045, 0.355, 1)';

const Page = styled.div`
  min-height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 56px 24px;
  box-sizing: border-box;
  background: ${theme.colors.landingPageBeige};
`;

const StickerWrap = styled.div`
  width: 168px;
  margin-bottom: 24px;
  animation: ${float} 5s ${ease} infinite;
  filter: drop-shadow(0 12px 24px rgba(222, 175, 141, 0.35));

  svg {
    display: block;
    width: 100%;
    height: auto;
  }
`;

const Headline = styled.h1`
  margin: 0;
  font-size: 42px;
  font-weight: 900;
  line-height: 1.15;
  letter-spacing: -0.01em;
  color: ${theme.colors.black};

  @media ${theme.device.mobileL} {
    font-size: 32px;
  }
`;

const Body = styled.p`
  margin: 16px 0 0;
  max-width: 520px;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.5;
  color: rgba(40, 47, 51, 0.72);

  @media ${theme.device.mobileL} {
    font-size: 17px;
  }
`;

const TryAgainButton = styled.button`
  margin-top: 32px;
  border: none;
  cursor: pointer;
  border-radius: 5px;
  padding: 15px 40px;
  font-family: inherit;
  font-size: 18px;
  font-weight: 700;
  color: #fff;
  background: ${theme.colors.orange};
  box-shadow: 0 4px 16px rgba(222, 175, 141, 0.55);
  transition: background 0.4s ${ease}, box-shadow 0.3s ${ease};

  &:hover {
    background: ${theme.colors.orangeDark};
    box-shadow: 0 2px 8px rgba(222, 175, 141, 0.5);
  }
`;

const SupportLine = styled.p`
  margin: 16px 0 0;
  font-size: 15px;
  font-weight: 700;
  color: ${theme.colors.text.darkGrey};

  a {
    color: ${theme.colors.orangeDark};
    text-decoration: none;

    &:hover {
      color: ${theme.colors.black};
      text-decoration: underline;
    }
  }
`;

export default ErrorPage;
